/**
 * Offline Queue — IndexedDB-backed mutation buffer
 *
 * Fixes applied:
 * - Added MAX_RETRIES and retry_count per item to prevent permanent retry loops
 * - Added replayLock to prevent concurrent replayQueue() calls (flapping network race)
 * - Added session_id to queued items for future validation
 * - Failed items beyond MAX_RETRIES are removed from the queue (not retried forever)
 */

// Lazy import betaLogger to avoid circular deps — betaLogger doesn't import offlineQueue
let _betaLogger = null;
const getBetaLogger = async () => {
  if (_betaLogger) return _betaLogger;
  try { _betaLogger = await import('./betaLogger.js'); } catch {}
  return _betaLogger;
};

const DB_NAME    = 'splitsnap-offline';
const STORE      = 'queue';
const VERSION    = 2; // bumped for new retry_count field
const MAX_RETRIES = 5;

let _db         = null;
let _replayLock = false; // prevents concurrent replay calls

const openDB = () => new Promise((resolve, reject) => {
  if (_db) return resolve(_db);
  const req = indexedDB.open(DB_NAME, VERSION);
  req.onupgradeneeded = (e) => {
    const db = e.target.result;
    if (!db.objectStoreNames.contains(STORE)) {
      db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
    }
  };
  req.onsuccess = (e) => { _db = e.target.result; resolve(_db); };
  req.onerror   = ()  => reject(req.error);
});

export const enqueue = async (action) => {
  try {
    const db = await openDB();
    await new Promise((res, rej) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).add({ ...action, ts: Date.now(), retry_count: 0 });
      tx.oncomplete = () => res();
      tx.onerror    = () => rej(tx.error);
    });
  } catch (err) {
    // Enqueue failure is non-fatal — the user action already happened optimistically
    console.warn('[OfflineQueue] enqueue failed:', err);
  }
};

const dequeue = async () => {
  const db = await openDB();
  return new Promise((res, rej) => {
    const tx  = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => res(req.result);
    req.onerror   = () => rej(req.error);
  });
};

const updateRetryCount = async (id, count) => {
  try {
    const db = await openDB();
    await new Promise((res, rej) => {
      const tx    = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      const req   = store.get(id);
      req.onsuccess = () => {
        if (req.result) {
          store.put({ ...req.result, retry_count: count });
        }
        res();
      };
      req.onerror = () => rej(req.error);
    });
  } catch {}
};

const removeItem = async (id) => {
  try {
    const db = await openDB();
    await new Promise((res, rej) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(id);
      tx.oncomplete = () => res();
      tx.onerror    = () => rej(tx.error);
    });
  } catch {}
};

export const clearQueue = async () => {
  try {
    const db = await openDB();
    await new Promise((res, rej) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => res();
      tx.onerror    = () => rej(tx.error);
    });
  } catch {}
};

/**
 * Replay all queued mutations.
 * Protected by a lock so concurrent calls (flapping network) don't duplicate writes.
 */
export const replayQueue = async (supabase) => {
  if (_replayLock) {
    console.debug('[OfflineQueue] replay already in progress, skipping');
    return;
  }
  _replayLock = true;

  try {
    const items = await dequeue();
    if (!items.length) return;

    for (const item of items) {
      // Skip permanently failed items (will be cleaned up)
      if ((item.retry_count ?? 0) >= MAX_RETRIES) {
        await removeItem(item.id);
        console.warn('[OfflineQueue] abandoned item after max retries:', item);
        getBetaLogger().then((bl) => bl?.logSyncFailure?.({ reason: 'replay_abandon', retries: item.retry_count }));
        continue;
      }

      try {
        switch (item.type) {
          case 'CLAIM':
            await supabase
              .from('item_claims')
              .upsert(
                {
                  item_id:          item.item_id,
                  participant_id:   item.participant_id,
                  claimed_quantity: item.claimed_quantity ?? 1,
                },
                { onConflict: 'item_id,participant_id' }
              );
            break;
          case 'UNCLAIM':
            await supabase
              .from('item_claims')
              .delete()
              .match({ item_id: item.item_id, participant_id: item.participant_id });
            break;
          case 'MARK_PAID':
            await supabase
              .from('participants')
              .update({ status: item.status, paid_at: item.status === 'paid' ? new Date().toISOString() : null })
              .eq('id', item.participant_id);
            break;
          default:
            console.warn('[OfflineQueue] unknown action type:', item.type);
            await removeItem(item.id);
            continue;
        }
        await removeItem(item.id);
      } catch (err) {
        const newCount = (item.retry_count ?? 0) + 1;
        console.warn(`[OfflineQueue] replay failed (attempt ${newCount}/${MAX_RETRIES}):`, item, err);
        await updateRetryCount(item.id, newCount);
        if (newCount === 1) { // only log the first failure per item, not each retry
          getBetaLogger().then((bl) => bl?.logSyncFailure?.({ reason: 'replay_fail', retries: newCount }));
        }
      }
    }
  } finally {
    _replayLock = false;
  }
};

export const getQueueLength = async () => {
  try {
    const items = await dequeue();
    return items.length;
  } catch {
    return 0;
  }
};
